import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, EnrolledContact, api, parseTags } from "../api";
import TagChips from "./TagChips";

const stateColor: Record<string, string> = {
  queued: "bg-ink-100 text-ink-700",
  sending: "bg-amber-100 text-amber-800",
  sent: "bg-blue-100 text-blue-800",
  replied: "bg-emerald-100 text-emerald-800",
  won: "bg-green-200 text-green-900",
  lost: "bg-ink-100 text-ink-500",
  bounced: "bg-red-100 text-red-800",
  unsub: "bg-ink-200 text-ink-700",
  skipped: "bg-ink-100 text-ink-500",
};

const labelColor: Record<string, string> = {
  interested: "bg-emerald-100 text-emerald-800",
  meeting_request: "bg-green-100 text-green-800",
  pricing_question: "bg-amber-100 text-amber-800",
  objection: "bg-orange-100 text-orange-800",
  polite_decline: "bg-ink-100 text-ink-700",
  unsubscribe: "bg-red-100 text-red-800",
  out_of_office: "bg-ink-100 text-ink-500",
  wrong_person: "bg-ink-100 text-ink-700",
  spam_complaint: "bg-red-200 text-red-900",
  needs_human: "bg-purple-100 text-purple-800",
  other: "bg-ink-100 text-ink-500",
};

const STATE_FILTERS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "queued", label: "Queued" },
  { key: "sent", label: "Sent" },
  { key: "replied", label: "Replied" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
  { key: "bounced", label: "Bounced" },
  { key: "unsub", label: "Unsubscribed" },
];

export default function EnrolledContacts({ campaignId }: { campaignId: string }) {
  const [data, setData] = useState<{
    total: number;
    by_state: Record<string, number>;
    contacts: EnrolledContact[];
  } | null>(null);
  const [filter, setFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setErr(null);
    try {
      const url = `/api/campaigns/${campaignId}/contacts?limit=200${filter ? `&state=${filter}` : ""}`;
      const r = await api<{ total: number; by_state: Record<string, number>; contacts: EnrolledContact[] }>(url);
      setData(r);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, filter]);

  const remove = async (cc_id: string, email: string) => {
    if (!confirm(`Remove ${email} from this campaign?\n\nThis deletes the enrollment + any thread/messages for this contact in this campaign. The contact itself stays in your contacts list.`)) return;
    setBusy(true);
    try {
      await api(`/api/campaigns/${campaignId}/enrollments/${cc_id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const filteredContacts = useMemo(() => {
    if (!data) return [];
    if (!search) return data.contacts;
    const q = search.toLowerCase();
    return data.contacts.filter(
      (c) =>
        (c.email ?? "").toLowerCase().includes(q) ||
        (c.name ?? "").toLowerCase().includes(q) ||
        (c.company ?? "").toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="text-sm font-semibold">Enrolled contacts</h2>
        <div className="text-xs text-ink-500">{data?.total ?? "…"} total</div>
      </div>

      {err && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 mb-3">{err}</div>}

      {/* state filter chips */}
      <div className="flex flex-wrap gap-1 mb-3">
        {STATE_FILTERS.map((f) => {
          const count = f.key ? data?.by_state[f.key] ?? 0 : data?.total ?? 0;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                active ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-ink-200 hover:bg-ink-50"
              }`}
            >
              {f.label} <span className={active ? "text-ink-300" : "text-ink-400"}>{count}</span>
            </button>
          );
        })}
      </div>

      <input
        className="input mb-3"
        placeholder="Search email, name, company within this campaign…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {!data ? (
        <div className="text-sm text-ink-500">loading…</div>
      ) : filteredContacts.length === 0 ? (
        <div className="rounded-md border border-ink-200 bg-ink-50 p-6 text-center text-sm text-ink-500">
          No contacts match this filter.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-ink-200">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr className="text-left text-ink-500 text-xs uppercase tracking-wide">
                <th className="px-3 py-2 font-medium">Contact</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">AI label</th>
                <th className="px-3 py-2 font-medium">Replies</th>
                <th className="px-3 py-2 font-medium">Last activity</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {filteredContacts.map((c) => {
                const lastTs = c.last_inbound_at ?? c.sent_at ?? c.scheduled_for;
                return (
                  <tr key={c.campaign_contact_id} className="hover:bg-ink-50">
                    <td className="px-3 py-2 align-top">
                      <Link to={`/contacts/${c.contact_id}`} className="font-mono text-xs hover:underline">{c.email}</Link>
                      {(c.name || c.company) && (
                        <div className="text-xs text-ink-500 mt-0.5">
                          {c.name ?? ""}{c.name && c.company ? " · " : ""}{c.company ?? ""}
                        </div>
                      )}
                      {parseTags(c.tags).length > 0 && (
                        <div className="mt-1"><TagChips value={parseTags(c.tags)} readOnly /></div>
                      )}
                      {c.notes && (
                        <div className="text-xs text-ink-400 mt-0.5 italic truncate max-w-xs" title={c.notes}>
                          📝 {c.notes.slice(0, 60)}
                        </div>
                      )}
                      {c.last_error && (
                        <div className="text-xs text-red-600 mt-0.5 font-mono truncate max-w-xs" title={c.last_error}>
                          ⚠ {c.last_error}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`pill ${stateColor[c.state] ?? "bg-ink-100 text-ink-700"}`}>{c.state}</span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {c.last_label ? (
                        <>
                          <span className={`pill ${labelColor[c.last_label] ?? "bg-ink-100"}`}>{c.last_label}</span>
                          {c.last_confidence !== null && (
                            <div className="text-xs text-ink-400 mt-0.5">{c.last_confidence.toFixed(2)}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-ink-700">{c.reply_count}</td>
                    <td className="px-3 py-2 align-top text-xs text-ink-500">
                      {lastTs ? new Date(lastTs * 1000).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                      {c.thread_id && (
                        <Link to={`/threads/${c.thread_id}`} className="btn-secondary text-xs px-2 py-1 mr-1">
                          Thread
                        </Link>
                      )}
                      <button
                        onClick={() => remove(c.campaign_contact_id, c.email)}
                        className="btn-danger text-xs px-2 py-1"
                        disabled={busy}
                        title="Remove from this campaign"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
